import { RadioButton } from 'macaron-frontend-ui'
import React from 'react'
import { twMerge } from 'tailwind-merge'
import Image from '~/components/image/Image'
import { usePayPayConnectUrl } from '~/features/reservationInput/lib/url'
import { LOGIN_SITE, PAYPAY_IMAGE_URL } from '~/lib/constants/constants'
import { money } from '~/lib/money/money'
import { css, sva } from '~/styled-system/css'
import { useCanDisplayGivenPointsType, useGivenPoints } from '../../store/payment'
import { useIsGuest } from '../../store/stateMachine/state'
import { useDisplayGivenPoints } from './useDisplayGivenPoints'
import { useIkyuPoints } from './useIkyuPoints'
import { usePayPayPoints } from './usePayPayPoints'
import { useSave } from './useSave'
import { useUsageErrorMessage, useYahooConnectUrl } from './useUsageErrorMessage'
import { useUseInstantly } from './useUseInstantly'
import { useYahooConnect } from './useYahooConnect'

export const GivenPoints = import.meta.env.VITE_MODE === 'ppg' ? PpgGivenPoints : IkyuGivenPoints

const styles = sva({
  slots: ['container', 'head', 'title', 'point', 'content'],
  base: {
    container: {
      bg: 'white',
      paddingX: '3',
      paddingY: '4',
      spaceY: '3',
      _pc: {
        padding: '6',
      },
    },
    head: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontWeight: 'bold',
      fontSize: 'lg',
    },
    title: {
      fontSize: 'xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '2xl',
      },
    },
    point: {
      display: 'flex',
      alignItems: 'center',
    },
    content: {
      spaceY: '4',
      _pc: {
        w: '[424px]',
        ml: 'auto',
        border: 'medium',
        borderRadius: 'default',
        p: '4',
      },
    },
  },
})

function IkyuGivenPoints() {
  const { isGuest, givenPoints } = useGivenPointsComponent()
  if (isGuest) {
    return null
  }
  const classes = styles()
  return (
    <div className={classes.container}>
      <IkyuDisplayGivenPoints />
      {givenPoints > 0 && (
        <div className={classes.content}>
          <Save>
            <GivenPointsType />
          </Save>
          <UseInstantly />
          <UsageErrorMessage />
        </div>
      )}
    </div>
  )
}

function PpgGivenPoints() {
  const { isGuest, givenPoints } = useGivenPointsComponent()
  if (isGuest) {
    return null
  }
  const classes = styles()
  return (
    <div className={classes.container}>
      <PpgDisplayGivenPoints />
      {givenPoints > 0 && (
        <div className={classes.content}>
          <Save />
          <UseInstantly />
          <UsageErrorMessage />
        </div>
      )}
    </div>
  )
}

function useGivenPointsComponent() {
  const isGuest = useIsGuest()
  const givenPoints = useGivenPoints()
  return { isGuest, givenPoints }
}

function IkyuDisplayGivenPoints() {
  const { givenPoints, needsPayPayConnect, viewer } = useDisplayGivenPoints()
  const classes = styles()
  return (
    <div className="space-y-1">
      <div className={classes.head}>
        <h2 className={classes.title}>獲得ポイント</h2>
        <div className={classes.point}>
          {viewer?.loginSite === LOGIN_SITE.Yahoo && <PayPayPointLabel />}
          <span className="font-bold">{money(givenPoints)}円分</span>
        </div>
      </div>
      <PayPayConnect show={needsPayPayConnect} />
    </div>
  )
}

function PpgDisplayGivenPoints() {
  const { givenPoints, needsPayPayConnect } = useDisplayGivenPoints()
  const classes = styles()
  return (
    <div className="space-y-1">
      <div className={classes.head}>
        <h2 className={classes.title}>獲得ポイント</h2>
        <div className={classes.point}>
          <PayPayPointLabel />
          <span className="font-bold">{money(givenPoints)}円分</span>
        </div>
      </div>
      <PayPayConnect show={needsPayPayConnect} />
    </div>
  )
}

function PayPayPointLabel() {
  return (
    <div
      className={css({
        mr: '1',
        display: 'flex',
        alignItems: 'center',
        gap: '1',
      })}
    >
      <Image src={PAYPAY_IMAGE_URL} width={13} height={13} alt="PayPayイメージ" />
      <p className={css({ flex: '1', fontSize: 'base', fontWeight: 'normal' })}>PayPayポイント</p>
    </div>
  )
}

function PayPayConnect({ show }: { show: boolean }) {
  const url = usePayPayConnectUrl()
  if (!show) {
    return null
  }
  return (
    <p className="mb-3 text-sm text-neutral-700">
      ※獲得したPayPayポイントを利用するには、
      <a className="text-accent-blue" href={url()}>
        PayPayの登録・連携
      </a>
      が必要です。
    </p>
  )
}

function Save({ children }: { children?: React.ReactNode }) {
  const { disabled, checked, changeGivenPointsUsage, inactive } = useSave()
  return (
    <div className="space-y-2">
      <RadioButton
        name="givenPointsUsage"
        value="SAVE"
        checked={checked}
        disabled={disabled}
        inactive={inactive}
        onChange={changeGivenPointsUsage}
      >
        貯める
      </RadioButton>
      {children}
    </div>
  )
}

function GivenPointsType() {
  const display = useCanDisplayGivenPointsType()
  if (!display) {
    return null
  }
  return (
    <div className="ml-7 space-y-4 rounded bg-neutral-100 p-3">
      <PayPayPoints />
      <IkyuPoints />
    </div>
  )
}

function PayPayPoints() {
  const { checked, disabled, show, changeGivenPointsType, inactive } = usePayPayPoints()
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <RadioButton
          name="givenPointsType"
          value="PAYPAY"
          checked={checked}
          disabled={disabled}
          inactive={inactive}
          onChange={changeGivenPointsType}
          labelClassName="items-center"
        >
          <Image src={PAYPAY_IMAGE_URL} width={13} height={13} alt="PayPayイメージ" />
          <p className={twMerge('ml-1', show && 'text-neutral-500')}>PayPayポイント</p>
        </RadioButton>
      </div>
      <YahooConnect />
    </div>
  )
}

function YahooConnect() {
  const { show, url } = useYahooConnect()
  if (!show) {
    return null
  }
  return (
    <p className="text-sm text-neutral-700">
      ※PayPayポイントの獲得には、
      <a className="text-accent-blue" href={url()}>
        Yahoo! JAPAN ID
      </a>
      との連携が必要です。
    </p>
  )
}

function IkyuPoints() {
  const { checked, disabled, changeGivenPointsType, show, inactive } = useIkyuPoints()
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <RadioButton
          name="givenPointsType"
          value="IKYU"
          checked={checked}
          disabled={disabled}
          inactive={inactive}
          onChange={changeGivenPointsType}
        >
          一休ポイント
        </RadioButton>
      </div>
      <PayPayConnect show={show} />
    </div>
  )
}

function UseInstantly() {
  const { disabled, checked, changeGivenPointsUsage, inactive } = useUseInstantly()
  return (
    <div className={css({ mt: '4', _pc: { mt: '0' } })}>
      <RadioButton
        name="givenPointsUsage"
        value="USE_INSTANTLY"
        checked={checked}
        disabled={disabled}
        inactive={inactive}
        onChange={changeGivenPointsUsage}
      >
        今すぐ使う
      </RadioButton>
    </div>
  )
}

function UsageErrorMessage() {
  const { isSeatOnly, pointAvailable, hasError } = useUsageErrorMessage()
  return (
    <>
      {!pointAvailable && (
        <ErrorMessage>この店舗ではポイントの即時利用はご利用いただけません。</ErrorMessage>
      )}
      {isSeatOnly && (
        <ErrorMessage>
          お席のみのご予約の場合、ポイントの即時利用はご利用いただけません。
        </ErrorMessage>
      )}
      {hasError && (
        <div className="space-y-1">
          <ErrorMessage>エラーのため、ポイントを貯めることはできません。</ErrorMessage>
          <IkyuOnlyYidLoginPrompt />
        </div>
      )}
    </>
  )
}

function ErrorMessage({ children }: { children: React.ReactNode }) {
  return <div className={css({ mt: '3', fontSize: 'sm', color: 'error.text' })}>{children}</div>
}

const IkyuOnlyYidLoginPrompt =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyYidLoginPrompt : YidLoginPrompt

function YidLoginPrompt() {
  const url = useYahooConnectUrl()
  return (
    <p>
      再度
      <a href={url} className={css({ color: 'link' })}>
        Yahoo! JAPAN IDでログイン
      </a>
      すると貯める が選択できます。
    </p>
  )
}

function EmptyYidLoginPrompt() {
  // PPGの場合はメッセージを非表示にする
  // https://github.com/ikyucom/macaron-frontend/issues/5143#issue-2746811427
  return null
}
